const DB_NAME = 'bananasgpt-image-cache';
const DB_VERSION = 1;
const BLOBS_STORE = 'blobs';
const ALIASES_STORE = 'aliases';

const memoryBlobs = new Map();
const memoryAliases = new Map();
const inflight = new Map();
let dbPromise;

const requestResult = (request) =>
    new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });

const openDb = () => {
    if (typeof indexedDB === 'undefined') return Promise.resolve(null);
    if (!dbPromise) {
        dbPromise = new Promise((resolve) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);
            request.onupgradeneeded = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains(BLOBS_STORE)) {
                    db.createObjectStore(BLOBS_STORE, { keyPath: 'hash' });
                }
                if (!db.objectStoreNames.contains(ALIASES_STORE)) {
                    db.createObjectStore(ALIASES_STORE, { keyPath: 'key' });
                }
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => resolve(null);
            request.onblocked = () => resolve(null);
        });
    }
    return dbPromise;
};

const hashBlob = async (blob, key) => {
    if (!window.crypto?.subtle) {
        return `fallback-${key}`;
    }
    const digest = await window.crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
    return Array.from(new Uint8Array(digest))
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');
};

const readPersistent = async (key) => {
    try {
        const db = await openDb();
        if (!db) return null;
        const alias = await requestResult(
            db.transaction(ALIASES_STORE, 'readonly').objectStore(ALIASES_STORE).get(key)
        );
        if (!alias?.hash) return null;
        const record = await requestResult(
            db.transaction(BLOBS_STORE, 'readonly').objectStore(BLOBS_STORE).get(alias.hash)
        );
        if (!record?.blob) return null;
        memoryAliases.set(key, alias.hash);
        memoryBlobs.set(alias.hash, record.blob);
        return record.blob;
    } catch {
        return null;
    }
};

const writePersistent = async (key, hash, blob) => {
    try {
        const db = await openDb();
        if (!db) return;
        const transaction = db.transaction([BLOBS_STORE, ALIASES_STORE], 'readwrite');
        transaction.objectStore(BLOBS_STORE).put({
            hash,
            blob,
            size: blob.size,
            type: blob.type,
            cachedAt: Date.now(),
        });
        transaction.objectStore(ALIASES_STORE).put({ key, hash });
    } catch {
        // Кэш в памяти продолжает работать, даже если IndexedDB недоступен.
    }
};

export const getCachedImageBlob = async (key, fetchBlob) => {
    const knownHash = memoryAliases.get(key);
    if (knownHash && memoryBlobs.has(knownHash)) {
        return memoryBlobs.get(knownHash);
    }
    if (inflight.has(key)) return inflight.get(key);

    const pending = (async () => {
        const persisted = await readPersistent(key);
        if (persisted) return persisted;

        const blob = await fetchBlob();
        const hash = await hashBlob(blob, key);
        const canonicalBlob = memoryBlobs.get(hash) || blob;
        memoryBlobs.set(hash, canonicalBlob);
        memoryAliases.set(key, hash);
        void writePersistent(key, hash, canonicalBlob);
        return canonicalBlob;
    })();

    inflight.set(key, pending);
    try {
        return await pending;
    } finally {
        inflight.delete(key);
    }
};
